import React from 'react';
import './MonthStats.scss';

export interface MonthStatsData {
  units: {
    total: number;
    leased: number;
    vacant: number;
  };
  payments: {
    paid: number;
    arrears: number;
    notSet: number;
  };
}

interface MonthStatsProps {
  data?: MonthStatsData;
}

const mockData: MonthStatsData = {
  units: { total: 184, leased: 160, vacant: 24 },
  payments: { paid: 64, arrears: 22, notSet: 14 }
};

const MonthStats: React.FC<MonthStatsProps> = ({
                                                 data = mockData
                                               }) => {
  const baseClass = 'MonthStats';

  // Розрахунок відсотків для units
  const unitsPercentages = {
    leased: Math.round((data.units.leased / data.units.total) * 100),
    vacant: Math.round((data.units.vacant / data.units.total) * 100)
  };

  // Розрахунок відсотків для payments
  const paymentsTotal = data.payments.paid + data.payments.arrears + data.payments.notSet;
  const paymentsPercentages = {
    paid: Math.round((data.payments.paid / paymentsTotal) * 100),
    arrears: Math.round((data.payments.arrears / paymentsTotal) * 100),
    notSet: Math.round((data.payments.notSet / paymentsTotal) * 100)
  };

  // Розрахунок кутів для SVG діаграм (360 градусів = 100%)
  const unitsAngles = {
    leased: (unitsPercentages.leased / 100) * 360,
    vacant: (unitsPercentages.vacant / 100) * 360
  };

  const paymentsAngles = {
    paid: (paymentsPercentages.paid / 100) * 360,
    arrears: (paymentsPercentages.arrears / 100) * 360,
    notSet: (paymentsPercentages.notSet / 100) * 360
  };

  // Функція для створення SVG path для кругової діаграми
  const createArcPath = (startAngle: number, endAngle: number, radius = 45, centerX = 52, centerY = 52) => {
    const start = polarToCartesian(centerX, centerY, radius, endAngle);
    const end = polarToCartesian(centerX, centerY, radius, startAngle);
    const largeArcFlag = endAngle - startAngle <= 180 ? "0" : "1";

    return [
      "M", centerX, centerY,
      "L", start.x, start.y,
      "A", radius, radius, 0, largeArcFlag, 0, end.x, end.y,
      "Z"
    ].join(" ");
  };

  const polarToCartesian = (centerX: number, centerY: number, radius: number, angleInDegrees: number) => {
    const angleInRadians = (angleInDegrees - 90) * Math.PI / 180.0;
    return {
      x: centerX + (radius * Math.cos(angleInRadians)),
      y: centerY + (radius * Math.sin(angleInRadians))
    };
  };

  const UnitsChart = () => {
    let currentAngle = 0;
    const leasedPath = createArcPath(currentAngle, currentAngle + unitsAngles.leased);
    currentAngle += unitsAngles.leased;
    const vacantPath = createArcPath(currentAngle, currentAngle + unitsAngles.vacant);

    return (
      <svg width="104" height="104" viewBox="0 0 104 104" className={`${baseClass}__chart`}>
        <path d={leasedPath} fill="#5CB2EF" />
        <path d={vacantPath} fill="#105E96" />
        <circle cx="52" cy="52" r="20" fill="white" />
      </svg>
    );
  };

  const PaymentsChart = () => {
    let currentAngle = 0;
    const paidPath = createArcPath(currentAngle, currentAngle + paymentsAngles.paid);
    currentAngle += paymentsAngles.paid;
    const arrearsPath = createArcPath(currentAngle, currentAngle + paymentsAngles.arrears);
    currentAngle += paymentsAngles.arrears;
    const notSetPath = createArcPath(currentAngle, currentAngle + paymentsAngles.notSet);

    return (
      <svg width="104" height="104" viewBox="0 0 104 104" className={`${baseClass}__chart`}>
        <path d={paidPath} fill="#76C102" />
        <path d={arrearsPath} fill="#FB2F54" />
        <path d={notSetPath} fill="#FF9C07" />
        <circle cx="52" cy="52" r="20" fill="white" />
      </svg>
    );
  };

  return (
    <div className={baseClass}>
      <h3 className={`${baseClass}__title`}>This month</h3>
      <div className={`${baseClass}__divider`} />

      <div className={`${baseClass}__content`}>
        {/* Units Statistics */}
        <div className={`${baseClass}__stat-block`}>
          <div className={`${baseClass}__data`}>
            <div className={`${baseClass}__categories`}>
              <div className={`${baseClass}__category`}>
                <div className={`${baseClass}__dot`} />
                <span className={`${baseClass}__category-label`}>Units</span>
              </div>
              <div className={`${baseClass}__category`}>
                <div className={`${baseClass}__dot ${baseClass}__dot--leased`} />
                <span className={`${baseClass}__category-label`}>Leased</span>
              </div>
              <div className={`${baseClass}__category`}>
                <div className={`${baseClass}__dot ${baseClass}__dot--vacant`} />
                <span className={`${baseClass}__category-label`}>Vacant</span>
              </div>
            </div>
            <div className={`${baseClass}__values`}>
              <div className={`${baseClass}__value`}>{data.units.total}</div>
              <div className={`${baseClass}__value`}>{data.units.leased}</div>
              <div className={`${baseClass}__value`}>{data.units.vacant}</div>
            </div>
          </div>
          <div className={`${baseClass}__chart-container`}>
            <UnitsChart />
          </div>
        </div>

        {/* Payments Statistics */}
        <div className={`${baseClass}__stat-block`}>
          <div className={`${baseClass}__data`}>
            <div className={`${baseClass}__categories`}>
              <div className={`${baseClass}__category`}>
                <div className={`${baseClass}__dot ${baseClass}__dot--paid`} />
                <span className={`${baseClass}__category-label`}>Paid</span>
              </div>
              <div className={`${baseClass}__category`}>
                <div className={`${baseClass}__dot ${baseClass}__dot--arrears`} />
                <span className={`${baseClass}__category-label`}>Arrears</span>
              </div>
              <div className={`${baseClass}__category`}>
                <div className={`${baseClass}__dot ${baseClass}__dot--not-set`} />
                <span className={`${baseClass}__category-label`}>Not set</span>
              </div>
            </div>
            <div className={`${baseClass}__values`}>
              <div className={`${baseClass}__value ${baseClass}__value--percentage`}>{paymentsPercentages.paid}%</div>
              <div className={`${baseClass}__value ${baseClass}__value--percentage`}>{paymentsPercentages.arrears}%</div>
              <div className={`${baseClass}__value ${baseClass}__value--percentage`}>{paymentsPercentages.notSet}%</div>
            </div>
          </div>
          <div className={`${baseClass}__chart-container`}>
            <PaymentsChart />
          </div>
        </div>
      </div>
    </div>
  );
};

export default MonthStats;